import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SessionService } from '../../_services/session/session.service';
import { BaseComponent } from '../../components/base/base.component';
import { PatientLinkComponent } from '../../components/misc/patient-link/patient-link.component';
import { DashboardDataService } from './dashboard-data.service';
import {
  ComparisonMetric,
  DashboardOverviewData,
  DashboardPeriodPreset,
} from './dashboard.models';

@Component({
  standalone: true,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    PatientLinkComponent,
  ],
})
export class DashboardComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  readonly presets: Array<{
    value: DashboardPeriodPreset;
    labelKey: string;
  }> = [
    { value: 'today', labelKey: 'DASHBOARD_OP.FILTERS.TODAY' },
    { value: 'week', labelKey: 'DASHBOARD_OP.FILTERS.WEEK' },
    { value: 'month', labelKey: 'DASHBOARD_OP.FILTERS.MONTH' },
    { value: 'year', labelKey: 'DASHBOARD_OP.FILTERS.YEAR' },
    { value: 'all', labelKey: 'DASHBOARD_OP.FILTERS.ALL_TIME' },
  ];
  readonly paymentsPageSize = 5;

  preset: DashboardPeriodPreset = 'month';
  anchorDate = '';
  paymentsPage = 1;
  treatmentsPage = 1;
  treatmentsSort: 'appointments' | 'revenue' | 'hours' | 'revenuePerHour' =
    'revenue';
  treatmentsOrder: 'asc' | 'desc' = 'desc';
  birthdaysPage = 1;
  readonly birthdaysPageSize = 5;
  cancellationsPage = 1;
  readonly cancellationsPageSize = 5;
  data: DashboardOverviewData | null = null;
  loading = false;
  loadError = false;

  private readonly subscriptions: Subscription[] = [];
  private requestVersion = 0;

  constructor(
    public session: SessionService,
    private readonly dashboardData: DashboardDataService,
    private readonly translate: TranslateService,
    private readonly router: Router,
  ) {
    super(session);
    this.anchorDate = this.dashboardData.today();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.load();
    this.subscriptions.push(
      this.session.getSite$().subscribe(() => {
        this.customer = this.session.getCustomer();
        this.customers_id = this.customer?.id;
        this.site = this.session.getSite();
        this.sites_id = this.site?.id;
        this.paymentsPage = 1;
        this.treatmentsPage = 1;
        this.birthdaysPage = 1;
        this.cancellationsPage = 1;
        this.load();
      }),
    );
  }

  override ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    super.ngOnDestroy();
  }

  load(): void {
    if (!this.customers_id || !this.sites_id) return;
    const version = ++this.requestVersion;
    this.loading = true;
    this.loadError = false;
    this.dashboardData
      .getOverview(this.customers_id, this.sites_id, {
        preset: this.preset,
        anchorDate: this.anchorDate,
        paymentsPage: this.paymentsPage,
        paymentsPageSize: this.paymentsPageSize,
        treatmentsPage: this.treatmentsPage,
        treatmentsSort: this.treatmentsSort,
        treatmentsOrder: this.treatmentsOrder,
      })
      .subscribe({
        next: (data) => {
          if (version !== this.requestVersion) return;
          this.data = data;
          this.birthdaysPage = 1;
          this.cancellationsPage = 1;
          this.loading = false;
        },
        error: () => {
          if (version !== this.requestVersion) return;
          this.loadError = true;
          this.loading = false;
        },
      });
  }

  setPreset(preset: DashboardPeriodPreset): void {
    if (this.preset === preset) return;
    this.preset = preset;
    this.anchorDate = this.dashboardData.today();
    this.paymentsPage = 1;
    this.treatmentsPage = 1;
    this.load();
  }

  shiftPeriod(direction: -1 | 1): void {
    if (this.preset === 'all') return;
    this.anchorDate = this.dashboardData.shiftAnchor(
      this.anchorDate,
      this.preset,
      direction,
    );
    this.paymentsPage = 1;
    this.treatmentsPage = 1;
    this.load();
  }

  goToToday(): void {
    this.anchorDate = this.dashboardData.today();
    this.paymentsPage = 1;
    this.treatmentsPage = 1;
    this.load();
  }

  changePaymentsPage(page: number): void {
    const totalPages =
      this.data?.financial.movements.pagination.totalPages || 1;
    if (page < 1 || page > totalPages || page === this.paymentsPage) return;
    this.paymentsPage = page;
    this.load();
  }

  changeTreatmentsPage(page: number): void {
    const totalPages = this.data?.treatmentRanking.pagination.totalPages || 1;
    if (page < 1 || page > totalPages || page === this.treatmentsPage) return;
    this.treatmentsPage = page;
    this.load();
  }

  setTreatmentSort(
    sort: 'appointments' | 'revenue' | 'hours' | 'revenuePerHour',
  ): void {
    if (this.treatmentsSort === sort) {
      this.treatmentsOrder = this.treatmentsOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.treatmentsSort = sort;
      this.treatmentsOrder = 'desc';
    }
    this.treatmentsPage = 1;
    this.load();
  }

  get visibleBirthdays() {
    const birthdays = this.data?.birthdays || [];
    const start = (this.birthdaysPage - 1) * this.birthdaysPageSize;
    return birthdays.slice(start, start + this.birthdaysPageSize);
  }

  get birthdaysTotalPages(): number {
    return Math.max(
      1,
      Math.ceil((this.data?.birthdays.length || 0) / this.birthdaysPageSize),
    );
  }

  changeBirthdaysPage(page: number): void {
    if (page < 1 || page > this.birthdaysTotalPages) return;
    this.birthdaysPage = page;
  }

  get visibleCancellations() {
    const cancellations = this.data?.cancellations.recent || [];
    const start = (this.cancellationsPage - 1) * this.cancellationsPageSize;
    return cancellations.slice(start, start + this.cancellationsPageSize);
  }

  get cancellationsTotalPages(): number {
    return Math.max(
      1,
      Math.ceil(
        (this.data?.cancellations.recent.length || 0) /
          this.cancellationsPageSize,
      ),
    );
  }

  changeCancellationsPage(page: number): void {
    if (page < 1 || page > this.cancellationsTotalPages) return;
    this.cancellationsPage = page;
  }

  get hasComparison(): boolean {
    return Boolean(this.data?.comparisonRange);
  }

  metricClass(metric: ComparisonMetric): string {
    if (metric.change === null || metric.change === 0) return 'neutral';
    return metric.change > 0 ? 'positive' : 'negative';
  }

  inverseChangeClass(change: number): string {
    if (!change) return 'neutral';
    return change < 0 ? 'positive' : 'negative';
  }

  formatChange(change: number | null, suffix = '%'): string {
    if (change === null)
      return this.translate.instant('DASHBOARD_OVERVIEW.NO_REFERENCE');
    const sign = change > 0 ? '+' : '';
    return `${sign}${new Intl.NumberFormat(this.locale(), { maximumFractionDigits: 1 }).format(change)}${suffix}`;
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat(this.locale(), {
      style: 'currency',
      currency:
        this.data?.financial.currency ||
        String(this.site?.currency || 'EUR').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat(this.locale(), {
      maximumFractionDigits: 1,
    }).format(value || 0);
  }

  formatDate(value: string, includeYear = false): string {
    const date =
      value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
    return new Intl.DateTimeFormat(this.locale(), {
      day: '2-digit',
      month: 'short',
      ...(includeYear ? { year: 'numeric' as const } : {}),
    }).format(date);
  }

  formatTime(value: string): string {
    return new Intl.DateTimeFormat(this.locale(), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  translatePaymentMethod(method: string | null | undefined): string {
    if (!method) return '—';

    const normalizedMethod = method
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
    const aliases: Record<string, string> = {
      CREDIT_CARD: 'CARD',
      CREDITCARD: 'CARD',
      TARJETA: 'CARD',
      EFECTIVO: 'CASH',
      OTRO: 'OTHER',
    };
    const paymentType = aliases[normalizedMethod] || normalizedMethod;
    const translationKey = `PAYMENT_TYPES.${paymentType}`;
    const translated = this.translate.instant(translationKey);

    return translated !== translationKey ? translated : method;
  }

  birthdayLabel(daysUntil: number): string {
    if (daysUntil === 0)
      return this.translate.instant('DASHBOARD_OP.PLANNING.BIRTHDAY_TODAY');
    if (daysUntil === 1)
      return this.translate.instant('DASHBOARD_OP.PLANNING.BIRTHDAY_TOMORROW');
    return this.translate.instant('DASHBOARD_OP.PLANNING.BIRTHDAY_IN_DAYS', {
      days: daysUntil,
    });
  }

  viewUnpaidIncidents(): void {
    this.navigateToIncidents('UNPAID_TREATMENT');
  }

  viewDashboardIncidents(): void {
    this.navigateToIncidents();
  }

  incidentReasons(reasons: Record<string, number>): Array<{
    key: string;
    label: string;
    count: number;
  }> {
    return Object.entries(reasons)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({
        key,
        label: `DASHBOARD_OP.REASONS.${key}`,
        count,
      }));
  }

  private navigateToIncidents(reason?: string): void {
    if (!this.customer || !this.site) return;
    const customer = this.customer.identifier || this.customer.id;
    const site = this.site.identifier || this.site.id;
    this.router.navigate([`/${customer}/${site}/incidents`], {
      queryParams: {
        reason: reason || null,
        status: 'active',
        datePreset: 'custom',
        dateFrom: this.data?.range.dateFrom || null,
        dateTo: this.data?.range.dateTo || null,
      },
    });
  }

  private locale(): string {
    const language = this.translate.currentLang || 'es';
    return language === 'ca' ? 'ca-ES' : language === 'en' ? 'en-GB' : 'es-ES';
  }
}
